import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { SanctionCheckResult } from '../../services/complianceService';

interface SanctionsDetailCardProps {
  result: SanctionCheckResult;
  index: number;
}

const SanctionsDetailCard: React.FC<SanctionsDetailCardProps> = ({ result, index }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const handleCopyJSON = () => {
    const jsonString = JSON.stringify(result, null, 2);
    navigator.clipboard.writeText(jsonString);
    toast.success('Sanctions result copied to clipboard');
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        border: result.is_sanctioned ? '2px solid' : '1px solid',
        borderColor: result.is_sanctioned ? theme.palette.error.main : 'divider',
        bgcolor: result.is_sanctioned ? theme.palette.error.light : theme.palette.background.paper,
        '& .MuiCardContent-root': {
          pb: 2,
        },
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {result.entity_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={result.is_sanctioned ? 'SANCTIONED' : 'CLEAR'}
                color={result.is_sanctioned ? 'error' : 'success'}
                icon={result.is_sanctioned ? <WarningIcon /> : <CheckIcon />}
              />
              <Chip
                label={`Match: ${(result.match_score || 0).toFixed(0)}%`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={result.entity_type}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
          <Tooltip title="Copy full JSON">
            <IconButton onClick={handleCopyJSON} size="small">
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Warning message */}
        {result.warning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {result.warning}
          </Alert>
        )}

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Lists Checked
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {result.lists_checked?.length || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Matches Found
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {result.match_count || 0}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              API Version
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {result.api_version || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Credential Source
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {result.credential_source || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Expandable Details */}
        <Box>
          {/* Request Parameters */}
          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">Request Parameters</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Entity Name:</strong></TableCell>
                    <TableCell>{result.entity_name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Entity Type:</strong></TableCell>
                    <TableCell>{result.entity_type}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Threshold:</strong></TableCell>
                    <TableCell>{result.threshold_used || 'N/A'}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Fuzzy Matching:</strong></TableCell>
                    <TableCell>
                      {result.fuzzy_matching_used !== undefined
                        ? result.fuzzy_matching_used
                          ? '✅ Enabled'
                          : '❌ Disabled'
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>

          {/* Lists Checked */}
          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">
                  Lists Checked ({result.lists_checked?.length || 0} total)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                All Sanctions Lists Screened:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {result.lists_checked && result.lists_checked.length > 0 ? (
                  result.lists_checked.map((list, idx) => {
                    const isMatched = result.matched_lists?.includes(list);
                    return (
                      <Chip
                        key={idx}
                        label={list}
                        size="small"
                        color={isMatched ? 'error' : 'default'}
                        variant={isMatched ? 'filled' : 'outlined'}
                      />
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No lists information available
                  </Typography>
                )}
              </Box>

              {result.matched_lists && result.matched_lists.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ color: theme.palette.error.main, mt: 2 }} gutterBottom>
                    ⚠️ Matched Lists ({result.matched_lists.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {result.matched_lists.map((list, idx) => (
                      <Chip
                        key={idx}
                        label={list}
                        size="small"
                        color="error"
                      />
                    ))}
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Match Details */}
          {result.matches && result.matches.length > 0 && (
            <Accordion disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                  <Typography variant="subtitle2" sx={{ color: theme.palette.error.main }}>
                    Match Details ({result.matches.length} matches)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {result.matches.map((match, idx) => (
                  <Card key={idx} variant="outlined" sx={{ mb: 1, bgcolor: theme.palette.error.light }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Match #{idx + 1}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {match.name && (
                            <TableRow>
                              <TableCell width="30%"><strong>Name:</strong></TableCell>
                              <TableCell>{match.name}</TableCell>
                            </TableRow>
                          )}
                          {match.score !== undefined && (
                            <TableRow>
                              <TableCell><strong>Score:</strong></TableCell>
                              <TableCell>{match.score}%</TableCell>
                            </TableRow>
                          )}
                          {match.list && (
                            <TableRow>
                              <TableCell><strong>List:</strong></TableCell>
                              <TableCell>
                                <Chip label={match.list} size="small" color="error" />
                              </TableCell>
                            </TableRow>
                          )}
                          {match.reason && (
                            <TableRow>
                              <TableCell><strong>Reason:</strong></TableCell>
                              <TableCell>{match.reason}</TableCell>
                            </TableRow>
                          )}
                          {match.details && (
                            <TableRow>
                              <TableCell><strong>Details:</strong></TableCell>
                              <TableCell>
                                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(match.details, null, 2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* API Metadata */}
          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">API Metadata</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell width="40%"><strong>Search ID:</strong></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {result.search_id || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>API Version:</strong></TableCell>
                    <TableCell>{result.api_version || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Checked At:</strong></TableCell>
                    <TableCell>{formatDate(result.checked_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Source:</strong></TableCell>
                    <TableCell>{result.source || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Credential Source:</strong></TableCell>
                    <TableCell>
                      <Chip
                        label={result.credential_source || 'N/A'}
                        size="small"
                        color={result.credential_source === 'user' ? 'primary' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SanctionsDetailCard;
